/**
 * Duplicate-judul detection. Titles are compared on a normalized form
 * (lowercase, punctuation stripped, whitespace collapsed) and scored with a
 * Levenshtein-based similarity. Guards against re-using a title that is already
 * taken by another (or a past) skripsi / approved pengajuan judul.
 */

/** Collapse a judul to a comparable key: lowercase, no punctuation, single spaces. */
export function normalizeJudul(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Levenshtein distance between two strings (plain DP, fine for short titles). */
export function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  const m = a.length;
  const n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  const prev = new Array<number>(n + 1);
  const curr = new Array<number>(n + 1);
  for (let j = 0; j <= n; j++) prev[j] = j;
  for (let i = 1; i <= m; i++) {
    curr[0] = i;
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
    }
    for (let j = 0; j <= n; j++) prev[j] = curr[j];
  }
  return prev[n];
}

/** Similarity in [0, 1] between two judul (1 = identical). */
export function judulSimilarity(a: string, b: string): number {
  const x = normalizeJudul(a);
  const y = normalizeJudul(b);
  if (!x || !y) return 0;
  const dist = levenshtein(x, y);
  return 1 - dist / Math.max(x.length, y.length);
}

export interface SimilarJudulMatch {
  /** The candidate title that collides. */
  title: string;
  /** Similarity score 0..1. */
  similarity: number;
}

/**
 * Return the best matching candidate among `candidates` whose similarity is at
 * or above `threshold` (default 0.85). Returns undefined when nothing collides.
 */
export function findSimilarJudul(
  candidates: string[],
  value: string,
  threshold = 0.85,
): SimilarJudulMatch | undefined {
  let best: SimilarJudulMatch | undefined;
  for (const c of candidates) {
    if (!c) continue;
    const sim = judulSimilarity(c, value);
    if (sim >= threshold && (!best || sim > best.similarity)) {
      best = { title: c, similarity: sim };
    }
  }
  return best;
}
